import { Outlet, useLocation } from 'react-router-dom'
import { useEffect, useLayoutEffect, useRef } from 'react'
import TabBar from './TabBar'
import useIdleTimeout from '../../hooks/useIdleTimeout'
import usePrivacyScreen from '../../hooks/usePrivacyScreen'
import IdleTimeoutModal from '../IdleTimeoutModal'
import PrivacyScreen from '../PrivacyScreen'
import { useUserStore } from '../../stores'
import { consumeProfileRunAtFirstHome } from '../../utils/firstHome'

export default function MobileLayout() {
  // PROD-1192 (L2a): the first main-app screen consumes the setup-run marker. A layout effect runs
  // before every passive effect, so before useIdleTimeout's mount effect reads the idle stamp.
  useLayoutEffect(() => { consumeProfileRunAtFirstHome() }, [])
  const { showWarning, secondsLeft, stayLoggedIn, performLogout, willLock } = useIdleTimeout()
  // v242.130.0: the away/return verdict is decided in usePrivacyScreen via the
  // pure `decideOnReturn`, and a verdict of "too long" is now an actual logout —
  // not an overlay carrying a Log in button while the token stayed in storage.
  const { showPrivacyOverlay, dismissOverlay } = usePrivacyScreen()
  // v239.6.0: pull fresh session-PIN state once per authenticated session so the
  // idle/away lock-vs-logout decision is never made on a stale flag. (Previously
  // sessionPinEnabled could read "Off" on a fresh load until Settings refreshed it,
  // which silently forced a full re-login instead of a PIN resume.)
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)
  const refreshUser = useUserStore((s) => s.refreshUser)
  const refreshedRef = useRef(false)
  useEffect(() => {
    if (isAuthenticated && !refreshedRef.current) {
      refreshedRef.current = true
      refreshUser?.()
    }
    if (!isAuthenticated) refreshedRef.current = false
  }, [isAuthenticated, refreshUser])

  const { pathname } = useLocation()
  // v242.13.0: screens whose own header is navy (Doctors) fill the top
  // safe-area themselves, so the header's navy + light-circle pattern runs
  // continuously up behind the status bar. Don't reserve a white top inset for
  // those routes; every other route keeps it.
  // v242.28.0: navy sub-page headers (Terms, Privacy, Schedule-Followup, Care
  // Agreement, Case Timeline, Medical Note) also fill their own top inset now, so
  // the shell must not reserve a white strip above them.
  const NAVY_HEADER_ROUTES = [
  // v242.76.0 — /home joined this set when it moved to AppHeader, which absorbs
  // its own env(safe-area-inset-top). The name is now a misnomer: the set means
  // FILLS ITS OWN TOP INSET, not "is navy" — /home, /cases, /visits and
  // /profile are white AppHeader screens and still belong in it.
  // PROD-1194 part C: /case/detect paints its own sticky white status-bar band (DetectScreen
  // data-status-band), so the status bar never draws over its scrolled cards.
  '/home','/doctors', '/cases', '/visits', '/profile', '/case/describe/summary', '/case/describe/conditions', '/case/describe/warnings', '/case/home', '/case/detect', '/settings/terms', '/settings/privacy-policy', '/case/schedule-followup', '/case/agreement', '/case/encounters', '/case/note',
  // s124 (Dr. Rami, build 91 "Empty header space"): My Doctors' navy header pays its own inset (s123); it was missing here.
  '/doctors/mine']
  // s120 (Dr. Rami, build 89: "Header dead space when opening chat link to case home"): notification / chat links
  //   open /case/<uuid>, which renders CaseHome (CaseRoute) — its navy header pays its own inset, so the shell must
  //   not reserve a white band above it. UUID-shaped only: /case/<word> screens keep their own rules.
  const CASE_BY_ID = /^\/case\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  const fillsOwnTopInset = NAVY_HEADER_ROUTES.includes(pathname) || pathname.startsWith('/doctor/') || CASE_BY_ID.test(pathname)

  return (
    <div className="screen-min-h bg-slate-100 flex flex-col">
      {/* Responsive app shell:
          - Phone (portrait/landscape): fills full width, no constraints
          - Tablet: content centered at 768px, slate background fills edges
          - Desktop: same centered content, professional feel
      */}
      <div className="w-full max-w-3xl mx-auto screen-min-h bg-white flex flex-col relative lg:shadow-xl lg:border-x lg:border-slate-200" style={{ paddingTop: fillsOwnTopInset ? 0 : 'env(safe-area-inset-top, 0px)', '--shell-top': fillsOwnTopInset ? '0px' : 'env(safe-area-inset-top, 0px)' }}>
        {/* v242.80.0 (TF-37) — kept in step with TabBar's own inset expression.
            The bar is 4rem of content plus whatever it reserves below itself;
            this reserves the same so the last item on a page never hides behind
            it. The two are coupled by construction, not by convention. */}
        {/* ★★★ v242.91.0 (TF b37 #0) — TWO OVERFLOW DECLARATIONS THAT DESCRIBED AN
            ARCHITECTURE THAT NEVER EXISTED, AND THEY COST THE STICKY HEADER.

            `index.css:45` says it outright: ".scroll-wrapper — this is the ONLY
            element that scrolls". It is `App.jsx:353`, it has `height:100%`
            against a `#root` that is `height:100%; overflow:hidden`, and it is
            in fact the only scroller in the app. `App.jsx:347` already knows —
            it resets scroll by querying `.scroll-wrapper`, because `window.scrollTo`
            is a no-op under `body{overflow:hidden}`.

            But <main> carried `overflow-hidden` and its child carried
            `h-full overflow-auto`. That child's `h-full` is a percentage against
            <main>, whose height is set by its own content (the shell is
            `screen-min-h`, not `h-screen`, so the flex container never gets a
            definite height). The percentage resolves to `auto`, the div grows to
            full content height, and it NEVER SCROLLS — while still being a
            scroll container.

            `position: sticky` sticks to its nearest scroll container. So
            CaseHome's header, correctly given `sticky top-0 z-30` in v242.87.0,
            was anchored to a box that does not scroll, and scrolled away with
            everything else. v242.87.0 removed exactly this pattern from
            `CaseHome.jsx:1367` and did not notice the identical one a level up.

            ★★ MEASURED, NOT REASONED: a headless repro of this exact chain
            (index.css + .scroll-wrapper + shell + main + PageWrapper + header)
            puts the header at top:-600 after a 600px scroll on the shipped
            markup, and at top:0 with these two declarations removed. Removing
            EITHER ONE ALONE still fails — the survivor is still a dead scroll
            container between the header and .scroll-wrapper. Both must go.

            Horizontal clipping is not lost: `.scroll-wrapper` already carries
            `overflow-x: hidden` (`index.css:49`), which is the right level for it. */}
        <main className="flex-1" style={{ paddingBottom: 'calc(4rem + max(calc(env(safe-area-inset-bottom, 0px) - 14px), 0px))' }}>
          <Outlet />
        </main>
        <TabBar />
      </div>

      {/* HIPAA idle timeout warning — 30 min */}
      {showWarning && (
        <IdleTimeoutModal
          secondsLeft={secondsLeft}
          onStayLoggedIn={stayLoggedIn}
          onLogout={performLogout}
          willLock={willLock}
        />
      )}

      {/* v187.0: HIPAA privacy screen — hides PHI on tab switch */}
      {showPrivacyOverlay && (
        <PrivacyScreen onDismiss={dismissOverlay} />
      )}

      {/* v237.6.0: the session PIN lock screen is rendered by SessionLockGuard, the layout route
          above this one (PROD-1197), so routes outside this layout are locked as well. */}

      {/* s101: What's new moved from a pop-up here to the first item of /notifications (WhatsNewNotice). */}
    </div>
  )
}
